import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Camera, Check, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import uploadService from '../services/uploadService';
import { getImageUrl } from '../config/runtime';
import CameraModal from './CameraModal';

/**
 * StructuredDataForm - Renders a form based on form_fields from a card
 * Used for inspections, measurements, and other structured data capture
 */
const StructuredDataForm = ({ 
  formFields = [], 
  onSubmit, 
  onCancel,
  cardTitle = 'Complete Form',
  cardId = null,
  initialValues = null, // Submitted values from previous submission
  initialFields = null, // Submitted fields array from previous submission
  disabled = false, // When true, form is read-only (already submitted)
  demoMode = false // When true, show form with submit button but non-functional
}) => {
  // Initialize form data from submitted values if available, otherwise use defaults
  const getInitialFormData = useCallback(() => {
    if (initialValues && typeof initialValues === 'object') {
      // Use submitted values, but only for fields that exist in formFields
      const initial = {};
      formFields.forEach(field => {
        // Check if field exists in initialValues (including falsy values like 0, false, '')
        if (field.id in initialValues) {
          const initialValue = initialValues[field.id];
          // Preserve the value as-is, including falsy values (0, false, '')
          // Only use default if value is explicitly null or undefined
          if (initialValue === null || initialValue === undefined) {
            // Use default for null/undefined
            if (field.type === 'yes_no') {
              initial[field.id] = null;
            } else if (field.type === 'checkbox_list') {
              initial[field.id] = [];
            } else {
              initial[field.id] = '';
            }
          } else {
            // Preserve the actual value (including 0, false, '')
            initial[field.id] = initialValue;
          }
        } else {
          // Default value if field not in submitted data
          if (field.type === 'yes_no') {
            initial[field.id] = null;
          } else if (field.type === 'checkbox_list') {
            initial[field.id] = [];
          } else {
            initial[field.id] = '';
          }
        }
      });
      return initial;
    }
    
    // Default initialization
    const initial = {};
    formFields.forEach(field => {
      // Use pre-filled value from LLM if available
      if (field.value !== undefined && field.value !== null) {
        initial[field.id] = field.value;
      } else if (field.type === 'yes_no') {
        initial[field.id] = null; // null = not answered, true/false = yes/no
      } else if (field.type === 'select' && field.options && field.options.length > 0) {
        initial[field.id] = '';
      } else if (field.type === 'checkbox_list') {
        initial[field.id] = []; // Array of selected option values
      } else {
        initial[field.id] = '';
      }
    });
    return initial;
  }, [formFields, initialValues]);

  const getInitialPhotoUrls = useCallback(() => {
    if (initialValues && typeof initialValues === 'object') {
      // Extract photo URLs from initialValues
      const photoUrls = {};
      formFields.forEach(field => {
        if (field.type === 'photo' && initialValues[field.id]) {
          photoUrls[field.id] = initialValues[field.id];
        }
      });
      return photoUrls;
    }
    
    // Check for pre-filled values from LLM
    const photoUrls = {};
    formFields.forEach(field => {
      if (field.type === 'photo' && field.value) {
        photoUrls[field.id] = field.value;
      }
    });
    return photoUrls;
  }, [formFields, initialValues]);

  const [formData, setFormData] = useState(() => getInitialFormData());
  const [photoUrls, setPhotoUrls] = useState(() => getInitialPhotoUrls());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState({}); // { fieldId: true/false }
  const [cameraModalFieldId, setCameraModalFieldId] = useState(null); // Track which field's camera is open
  // Track the last initialValues and formFields to prevent unnecessary resets
  const lastInitialValuesRef = useRef(null);
  const lastFormFieldsRef = useRef(null);

  // Reset form when formFields prop changes OR when initialValues change (new submission)
  // But only reset if initialValues actually changed in value (not just reference equality)
  useEffect(() => {
    // Check if formFields changed (by comparing field IDs)
    const currentFormFieldsStr = JSON.stringify(formFields?.map(f => f.id) || []);
    const lastFormFieldsStr = JSON.stringify(lastFormFieldsRef.current?.map(f => f.id) || []);
    const formFieldsChanged = currentFormFieldsStr !== lastFormFieldsStr;
    
    // Deep compare initialValues to prevent resets on reference-only changes
    const currentInitialValuesStr = JSON.stringify(initialValues);
    const lastInitialValuesStr = JSON.stringify(lastInitialValuesRef.current);
    const initialValuesChanged = currentInitialValuesStr !== lastInitialValuesStr;
    
    // Reset if formFields changed OR if initialValues changed in value (not just reference)
    // This prevents form resets when allMessages updates but the submitted data is the same
    if (formFieldsChanged || initialValuesChanged) {
      const initial = getInitialFormData();
      const initialPhotos = getInitialPhotoUrls();
      setFormData(initial);
      setPhotoUrls(initialPhotos);
      setErrors({});
      lastInitialValuesRef.current = initialValues;
      lastFormFieldsRef.current = formFields;
    } else {
      // Update refs even if we don't reset, to track current values
      lastInitialValuesRef.current = initialValues;
      lastFormFieldsRef.current = formFields;
    }
  }, [formFields, initialValues, getInitialFormData, getInitialPhotoUrls]);

  const handleFieldChange = useCallback((fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Clear error for this field
    if (errors[fieldId]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  }, [errors]);

  const handlePhotoCapture = useCallback(async (fieldId, file) => {
    if (!file) return;
    
    setUploadingPhotos(prev => ({ ...prev, [fieldId]: true }));
    
    try {
      // Validate file first
      const validation = uploadService.validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        setUploadingPhotos(prev => ({ ...prev, [fieldId]: false }));
        return;
      }

      // Upload photo using upload service
      const uploadResult = await uploadService.uploadImage(file);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Photo upload failed');
      }

      const imageUrl = uploadResult.data?.url || uploadResult.url;
      
      setPhotoUrls(prev => ({ ...prev, [fieldId]: imageUrl }));
      // Only update formData for photo fields
      setFormData(prev => ({ ...prev, [fieldId]: imageUrl }));
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.message || 'Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhotos(prev => ({ ...prev, [fieldId]: false }));
    }
  }, []);

  const handleCameraPhotoCapture = useCallback(async (file, options = {}) => {
    if (!cameraModalFieldId || !file) return;
    
    // Close the camera modal
    setCameraModalFieldId(null);
    
    // Process the photo capture
    await handlePhotoCapture(cameraModalFieldId, file);
  }, [cameraModalFieldId, handlePhotoCapture]);

  const handleCloseCameraModal = useCallback(() => {
    setCameraModalFieldId(null);
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    formFields.forEach(field => {
      const value = formData[field.id];
      const photoUrl = photoUrls[field.id];
      
      if (field.required) {
        if (field.type === 'photo') {
          if (!photoUrl && !value) {
            newErrors[field.id] = `${field.label} is required`;
          }
        } else if (field.type === 'yes_no') {
          if (value === null || value === undefined) {
            newErrors[field.id] = `${field.label} is required`;
          }
        } else if (field.type === 'checkbox_list') {
          if (!Array.isArray(value) || value.length === 0) {
            newErrors[field.id] = `Please select at least one option`;
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.id] = `${field.label} is required`;
        }
      }

      // Number validation
      if (field.type === 'number' && value !== '' && value !== null && value !== undefined) {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          newErrors[field.id] = `${field.label} must be a number`;
        } else {
          if (field.min !== null && field.min !== undefined && numValue < field.min) {
            newErrors[field.id] = `${field.label} must be at least ${field.min}${field.unit ? ' ' + field.unit : ''}`;
          }
          if (field.max !== null && field.max !== undefined && numValue > field.max) {
            newErrors[field.id] = `${field.label} must be at most ${field.max}${field.unit ? ' ' + field.unit : ''}`;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, photoUrls, formFields]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare structured data with field metadata
      const structuredData = {
        form_card_id: cardId,
        fields: formFields.map(field => {
          // For photo fields, prefer photoUrls; for others, use formData
          const value = field.type === 'photo' 
            ? (photoUrls[field.id] || formData[field.id] || null)
            : (formData[field.id] !== undefined && formData[field.id] !== null && formData[field.id] !== '' 
                ? formData[field.id] 
                : null);
          
          return {
            id: field.id,
            label: field.label,
            type: field.type,
            value: value
          };
        }),
        values: { ...formData, ...photoUrls }, // Flattened values for easy access
        submitted_at: new Date().toISOString()
      };

      await onSubmit(structuredData);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, photoUrls, formFields, cardId, validateForm, onSubmit]);

  const renderField = useCallback((field, index) => {
    const value = formData[field.id];
    const photoUrl = photoUrls[field.id];
    const error = errors[field.id];
    const hasError = !!error;

    // Shared input styles - improved contrast for light and dark modes
    const inputBaseStyles = `
      w-full px-3.5 py-3 text-[14px] text-gray-900 dark:text-gray-100
      bg-white dark:bg-gray-800 
      border rounded-xl transition-all duration-200
      placeholder:text-gray-400 dark:placeholder:text-gray-500
      focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
      dark:focus:ring-blue-400/30 dark:focus:border-blue-400
    `;
    
    const inputErrorStyles = hasError 
      ? 'border-red-400 dark:border-red-500 focus:ring-red-500/30 focus:border-red-500' 
      : 'border-gray-300 dark:border-gray-600';

    // Render label with optional indicator - stronger contrast
    const renderLabel = (showRequired = true) => (
      <label className="flex items-baseline gap-1.5 text-[13px] font-semibold text-gray-800 dark:text-gray-200 mb-2">
        <span>{field.label}</span>
        {field.required && showRequired && (
          <span className="text-red-500 dark:text-red-400 text-xs">*</span>
        )}
        {!field.required && (
          <span className="text-gray-400 dark:text-gray-500 text-[11px] font-normal">(optional)</span>
        )}
        {field.unit && (
          <span className="text-gray-500 dark:text-gray-400 text-[11px] font-normal ml-1">({field.unit})</span>
        )}
      </label>
    );

    // Render help text
    const renderHelp = () => field.help_text && (
      <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-1.5">{field.help_text}</p>
    );

    // Render error
    const renderError = () => hasError && (
      <p className="text-[12px] text-red-600 dark:text-red-400 pt-1.5 flex items-center gap-1.5 font-medium">
        <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0"></span>
        {error}
      </p>
    );

    switch (field.type) {
      case 'number':
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel()}
            <input
              type="number"
              value={value !== undefined && value !== null ? value : ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              min={field.min}
              max={field.max}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              disabled={disabled}
              readOnly={disabled}
              className={`${inputBaseStyles} ${inputErrorStyles} ${disabled ? 'bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed opacity-75' : ''}`}
            />
            {renderHelp()}
            {renderError()}
          </div>
        );

      case 'text':
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel()}
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              disabled={disabled}
              readOnly={disabled}
              className={`${inputBaseStyles} ${inputErrorStyles} ${disabled ? 'bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed opacity-75' : ''}`}
            />
            {renderHelp()}
            {renderError()}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel()}
            <select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              disabled={disabled}
              className={`${inputBaseStyles} ${inputErrorStyles} ${disabled ? 'bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed opacity-75' : ''} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%239CA3AF%22%20d%3D%22M3%204.5L6%207.5L9%204.5%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_12px_center]`}
            >
              <option value="">Select {field.label.toLowerCase()}</option>
              {field.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {renderHelp()}
            {renderError()}
          </div>
        );

      case 'checkbox_list':
        // Value is an array of selected option strings
        const selectedOptions = Array.isArray(value) ? value : [];
        const toggleOption = (option) => {
          if (disabled) return;
          const isSelected = selectedOptions.includes(option);
          const newSelection = isSelected
            ? selectedOptions.filter(o => o !== option)
            : [...selectedOptions, option];
          handleFieldChange(field.id, newSelection);
        };
        
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel()}
            <div className="space-y-2">
              {field.options?.map((option, idx) => {
                const isSelected = selectedOptions.includes(option);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(option);
                    }}
                    disabled={disabled}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left
                      transition-all duration-200 active:scale-[0.98]
                      ${isSelected
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500 dark:border-emerald-400'
                        : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }
                      ${disabled ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {/* Checkbox indicator */}
                    <div className={`
                      w-5 h-5 rounded flex-shrink-0 flex items-center justify-center
                      transition-all duration-200
                      ${isSelected
                        ? 'bg-emerald-500 dark:bg-emerald-400'
                        : 'border-2 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700'
                      }
                    `}>
                      {isSelected && (
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      )}
                    </div>
                    {/* Option text */}
                    <span className={`
                      text-[14px] leading-snug flex-1
                      ${isSelected
                        ? 'text-emerald-800 dark:text-emerald-200 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                      }
                    `}>
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedOptions.length > 0 && (
              <p className="text-[12px] text-emerald-600 dark:text-emerald-400 mt-2 font-medium">
                {selectedOptions.length} selected
              </p>
            )}
            {renderHelp()}
            {renderError()}
          </div>
        );

      case 'yes_no':
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel(false)}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) handleFieldChange(field.id, true);
                }}
                disabled={disabled}
                className={`flex-1 px-4 py-2.5 rounded-xl text-[14px] font-semibold transition-all duration-200 ${
                  value === true
                    ? 'bg-emerald-500 text-white shadow-sm ring-2 ring-emerald-500/30'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                } ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) handleFieldChange(field.id, false);
                }}
                disabled={disabled}
                className={`flex-1 px-4 py-2.5 rounded-xl text-[14px] font-semibold transition-all duration-200 ${
                  value === false
                    ? 'bg-red-500 text-white shadow-sm ring-2 ring-red-500/30'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                } ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                No
              </button>
            </div>
            {/* {field.required && value === null && !disabled && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 pt-2">Please select an option</p>
            )} */}
            {renderHelp()}
            {renderError()}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel()}
            <input
              type="date"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              disabled={disabled}
              readOnly={disabled}
              className={`${inputBaseStyles} ${inputErrorStyles} ${disabled ? 'bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed opacity-75' : ''}`}
            />
            {renderHelp()}
            {renderError()}
          </div>
        );

      case 'photo':
        return (
          <div key={field.id} className="space-y-0">
            {renderLabel()}
            {uploadingPhotos[field.id] ? (
              <div className="flex items-center justify-center w-full py-10 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-[13px] text-gray-600 dark:text-gray-400">Uploading...</span>
                </div>
              </div>
            ) : photoUrl ? (
              <div className="relative group">
                <img 
                  src={getImageUrl(photoUrl)} 
                  alt={field.label}
                  className="w-full h-40 object-cover rounded-xl border border-gray-300 dark:border-gray-600"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPhotoUrls(prev => {
                        const next = { ...prev };
                        delete next[field.id];
                        return next;
                      });
                      setFormData(prev => ({ ...prev, [field.id]: '' }));
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-black/70 text-white rounded-lg opacity-0 group-hover:opacity-100 hover:bg-black/90 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) setCameraModalFieldId(field.id);
                }}
                disabled={disabled}
                className={`
                  flex flex-col items-center justify-center gap-2 w-full py-8 
                  border border-dashed rounded-xl transition-all duration-200
                  ${disabled 
                    ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75' 
                    : hasError 
                      ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20 cursor-pointer hover:border-red-500 hover:bg-red-100 dark:hover:bg-red-900/30' 
                      : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-blue-900/20'
                  }
                `}
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400">
                  Tap to capture photo
                </span>
              </button>
            )}
            {renderHelp()}
            {renderError()}
          </div>
        );

      default:
        return null;
    }
  }, [formData, photoUrls, errors, uploadingPhotos, handleFieldChange, disabled]);

  if (!formFields || formFields.length === 0) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="space-y-5">
      <div className="space-y-4" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        {formFields.map((field, index) => renderField(field, index))}
      </div>

      {(!disabled || demoMode) && (
        <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-1">
          <button
            type="submit"
            disabled={isSubmitting || demoMode}
            className="flex-1 px-5 py-3 text-[15px] sm:text-base font-bold text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2 min-h-[48px]"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                <span>Submit</span>
              </>
            )}
          </button>
          {onCancel && !demoMode && (
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all duration-200 disabled:opacity-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Camera Modal */}
      <CameraModal
        isOpen={cameraModalFieldId !== null}
        onClose={handleCloseCameraModal}
        onPhotoCapture={handleCameraPhotoCapture}
      />
    </form>
  );
};

export default StructuredDataForm;

